/**
 * Imports beyond the page that starts them. An export being uploaded goes in pieces, held here (not by the page): it
 * goes on while you're elsewhere in the app, after a dropped connection (by itself, from where the server got to) and,
 * the same file picked again, after a reload; closing the tab meanwhile asks first. An import being applied runs on the
 * server whoever watches: it's followed here, asked how it's going, so a chip anywhere says it and leads back.
 */
import { signal } from "@preact/signals";
import { ApiError, api, upload } from "../api/client.ts";
import type { ImportStatus } from "../api/types.gen.ts";
import { store, stored } from "./storage.ts";

/** Pieces this big: a dropped connection loses at most one. */
const PIECE = 8 * 1024 * 1024;
/** What this browser keeps of the upload under way, to go on with the same file after a reload. */
const KEY = "revisa.importUpload";
/** Waits before trying again after a dropped connection, longer each time (a connection coming back cuts it short). */
const RETRY_MS = [1000, 2000, 5000, 10_000, 30_000] as const;
/** How often an import being applied is asked how it's going. */
const POLL_MS = 1500;
/** An estimate of the time left only once this far along (a share, and milliseconds). */
const ETA_SHARE = 0.05;
const ETA_AFTER_MS = 3000;
const MS = 1000;

export interface Uploading {
  /** Where the imports go (`/spaces/…/imports`, or `/orgs/…/imports` for a new space). */
  base: string;
  /** The import page, to go back to. */
  page: string;
  file: File;
  /** The import, once the server has it. */
  id: string | null;
  /** Bytes sent: what the server has, and the piece on its way. */
  sent: number;
  /** `sending`; `waiting` for the connection (it goes on by itself); `reading` (all there, the server reads it);
   *  `failed` (`error` says why). */
  phase: "sending" | "waiting" | "reading" | "failed";
  error: string | null;
  /** When this visit began sending, and from which byte: the pace. */
  since: number;
  from: number;
}

/** The upload under way (or stopped by an error), if any. */
export const uploading = signal<Uploading | null>(null);

/** An upload just finished: its import, staged, for its page to show (taken by it). */
export const uploaded = signal<{ base: string; status: ImportStatus } | null>(null);

/** An import being applied, followed: its API path, its page, and how it is (none yet). */
export interface Following {
  path: string;
  page: string;
  status: ImportStatus | null;
}

export const following = signal<Following | null>(null);

interface Kept {
  id: string;
  base: string;
  name: string;
  size: number;
  modified: number;
}

function kept(): Kept | null {
  try {
    return JSON.parse(stored(KEY) ?? "null") as Kept | null;
  } catch {
    return null;
  }
}

let stop: AbortController | null = null;

const set = (patch: Partial<Uploading>) => {
  if (uploading.value) {
    uploading.value = { ...uploading.value, ...patch };
  }
};

/** Waits `ms`, or until the connection comes back, or it's stopped. */
const pause = (ms: number, abort: AbortSignal) =>
  new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(timer);
      removeEventListener("online", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    addEventListener("online", done);
    abort.addEventListener("abort", done, { once: true });
  });

/** A dropped connection (or a server that can't be reached), not an answer. */
const lost = (e: unknown) => e instanceof ApiError && e.status === 0;

/** The import uploaded: what the page shows next. */
function finished(base: string, s: ImportStatus) {
  store(KEY, null);
  uploading.value = null;
  uploaded.value = { base, status: s };
}

/** Whether `file` is the one an earlier upload to `base`, stopped, was sending (the same name, size and date). */
export function resumes(file: File, base: string, id?: string): boolean {
  const k = kept();
  return Boolean(
    k &&
      k.base === base &&
      k.name === file.name &&
      k.size === file.size &&
      k.modified === file.lastModified &&
      (id === undefined || k.id === id),
  );
}

/**
 * Uploads `file` to `base` as an import (`page`: its page), in pieces: going on from where an upload of the same file
 * stopped. Once it's all there and read, `uploaded` has the import.
 */
export async function startUpload(file: File, base: string, page: string): Promise<void> {
  if (uploading.value && uploading.value.phase !== "failed") {
    return;
  }
  stop?.abort();
  const ctl = new AbortController();
  stop = ctl;
  const abort = ctl.signal;
  uploaded.value = null;
  uploading.value = { base, page, file, id: null, sent: 0, phase: "sending", error: null, since: Date.now(), from: 0 };
  try {
    let id: string | null = null;
    let at = 0;
    const k = kept();
    if (k && resumes(file, base)) {
      const s = await api.get<ImportStatus>(`${base}/${k.id}`).catch(() => null);
      if (s?.state === "uploading") {
        ({ id } = s);
        at = s.received ?? 0;
      }
    }
    if (!id) {
      const s = await api.post<ImportStatus>(`${base}/uploads`, { name: file.name, size: file.size });
      ({ id } = s);
      store(KEY, JSON.stringify({ id, base, name: file.name, size: file.size, modified: file.lastModified }));
    }
    set({ id, sent: at, since: Date.now(), from: at });
    let tries = 0;
    for (;;) {
      const start = at;
      const end = Math.min(start + PIECE, file.size);
      try {
        const s = await upload<ImportStatus>(
          `${base}/${id}/data?offset=${start}`,
          file.slice(start, end),
          (n) => set({ sent: start + n, phase: start + n >= file.size ? "reading" : "sending" }),
          { method: "PUT", signal: abort },
        );
        tries = 0;
        if (s.state === "staged") {
          finished(base, s);
          return;
        }
        at = s.received ?? end;
        set({ sent: at, phase: "sending" });
      } catch (e) {
        if (abort.aborted) {
          return;
        }
        if (!(lost(e) || (e instanceof ApiError && e.code === "upload_offset"))) {
          throw e;
        }
        if (lost(e)) {
          set({ phase: "waiting" });
          await pause(RETRY_MS[Math.min(tries, RETRY_MS.length - 1)] ?? MS, abort);
          tries += 1;
        }
        // Where the server got to (what reached it of the piece counts).
        const s = await api.get<ImportStatus>(`${base}/${id}`).catch(() => null);
        if (s?.state === "staged") {
          finished(base, s);
          return;
        }
        at = s?.received ?? at;
        set({ sent: at, since: Date.now(), from: at });
      }
    }
  } catch (e) {
    if (abort.aborted) {
      return;
    }
    // Refused (not an export, too big): there's nothing to go on with.
    store(KEY, null);
    set({ phase: "failed", error: e instanceof Error ? e.message : String(e) });
  }
}

/** Stops the upload and drops what the server has of it. */
export function cancelUpload(): void {
  const u = uploading.value;
  stop?.abort();
  stop = null;
  uploading.value = null;
  store(KEY, null);
  if (u?.id) {
    api.del(`${u.base}/${u.id}`).catch(() => undefined);
  }
}

/** Forgets an upload that failed. */
export function dismissUpload(): void {
  if (uploading.value?.phase === "failed") {
    uploading.value = null;
  }
}

/** Seconds left at the pace so far, once it's far enough along to tell; else null. */
export function uploadEta(u: Uploading, now = Date.now()): number | null {
  const done = u.sent - u.from;
  const elapsed = now - u.since;
  if (u.phase !== "sending" || done <= 0 || elapsed < ETA_AFTER_MS || done < u.file.size * ETA_SHARE) {
    return null;
  }
  return Math.ceil(((u.file.size - u.sent) * elapsed) / done / MS);
}

let polling = false;

/** Follows the import at `path` (its page: `page`) until it's done or failed. */
export function follow(path: string, page: string, status: ImportStatus | null = null): void {
  if (following.value?.path !== path || status) {
    following.value = { path, page, status };
  }
  if (polling) {
    return;
  }
  polling = true;
  const poll = async () => {
    for (;;) {
      const f = following.value;
      if (!f || (f.status && f.status.state !== "applying")) {
        break;
      }
      await new Promise((r) => setTimeout(r, POLL_MS));
      try {
        const s = await api.get<ImportStatus>(f.path);
        if (following.value?.path === f.path) {
          following.value = { ...f, status: s };
        }
      } catch (e) {
        if (!lost(e)) {
          // Gone (dismissed elsewhere, or past its time): nothing to follow.
          if (following.value?.path === f.path) {
            following.value = null;
          }
        }
      }
    }
  };
  poll()
    .finally(() => {
      polling = false;
    })
    .catch(() => undefined);
}

/** Stops showing the import followed (it's done, and seen). */
export function unfollow(): void {
  following.value = null;
}

// Closing or reloading the tab mid-upload asks first (the upload can go on after, with the same file, but only then).
if (typeof addEventListener === "function") {
  addEventListener("beforeunload", (e) => {
    const u = uploading.value;
    if (u && u.phase !== "failed") {
      e.preventDefault();
    }
  });
}
